'use client';

import { useEffect, useState } from 'react';
import { Search, PhoneOff, RefreshCw, ChevronLeft, ChevronRight, CheckCircle, XCircle, Plus, Loader2 } from 'lucide-react';
import { adminApi, api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatNumber, formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';

interface NumberPool {
  id: string;
  providerId: string;
  providerName: string;
  phoneNumber: string;
  countryCode: string;
  areaCode: string | null;
  numberType: 'mobile' | 'landline' | 'toll_free' | 'voip' | 'shortcode';
  capabilities: ('sms' | 'voice' | 'mms')[];
  monthlyCost: number;
  setupCost: number;
  status: 'available' | 'assigned' | 'reserved' | 'quarantined' | 'porting_in' | 'porting_out';
  assignedTo: string | null;
  assignedUser: string | null;
  assignedAt: string | null;
  expiresAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface Assignment {
  id: string;
  userId: string;
  userEmail: string;
  numberPoolId: string;
  phoneNumber: string;
  status: 'active' | 'expired' | 'revoked' | 'pending';
  assignedAt: string;
  expiresAt: string | null;
  renewedAt: string | null;
  autoRenew: boolean;
  monthlyReward: number;
  createdAt: string;
  updatedAt: string;
}

export function AdminNumbersPage() {
  const [numbers, setNumbers] = useState<NumberPool[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [providers, setProviders] = useState<{id: string, name: string}[]>([]);
  const itemsPerPage = 20;

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignNumberId, setAssignNumberId] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [assignProviderId, setAssignProviderId] = useState('');
  const [assignAutoRenew, setAssignAutoRenew] = useState(true);
  const [assigning, setAssigning] = useState(false);

  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseAssignmentId, setReleaseAssignmentId] = useState('');
  const [releasing, setReleasing] = useState(false);

  const fetchNumbers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
        ...(countryFilter && { country: countryFilter }),
      });
      const response = await adminApi.getNumbers(params.toString()) as { items: NumberPool[]; totalPages: number } | null;
      if (response && response.items) {
        setNumbers(response.items);
        setTotalPages(response.totalPages);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await adminApi.getAssignments(`page=1&limit=100`) as { items: Assignment[] } | null;
      if (response && response.items) {
        setAssignments(response.items);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await adminApi.getProviders(`limit=100`) as { items: {id: string, displayName: string}[] } | null;
      if (response && response.items) {
        setProviders(response.items.map((p) => ({ id: p.id, name: p.displayName })));
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNumbers();
    fetchAssignments();
    fetchProviders();
  }, [currentPage, search, statusFilter, typeFilter, countryFilter]);

  const handleAssign = async () => {
    if (!assignUserId || !assignProviderId) {
      toast.error('Select user and provider');
      return;
    }
    setAssigning(true);
    try {
      const response = await api.post(`/admin/numbers/${assignNumberId}/assign`, {
        userId: assignUserId,
        providerId: assignProviderId,
        autoRenew: assignAutoRenew,
      });
      if (response) {
        toast.success('Number assigned');
        fetchNumbers();
        fetchAssignments();
        setAssignDialogOpen(false);
        setAssignNumberId('');
        setAssignUserId('');
      } else {
        toast.error('Failed to assign number');
      }
    } catch (err) {
      toast.error('Failed to assign number');
    } finally {
      setAssigning(false);
    }
  };

  const handleRelease = async () => {
    if (!releaseAssignmentId) return;
    setReleasing(true);
    try {
      const response = await api.post(`/admin/assignments/${releaseAssignmentId}/release`);
      if (response) {
        toast.success('Number released');
        fetchNumbers();
        fetchAssignments();
        setReleaseDialogOpen(false);
        setReleaseAssignmentId('');
      } else {
        toast.error('Failed to release number');
      }
    } catch (err) {
      toast.error('Failed to release number');
    } finally {
      setReleasing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available': return <Badge className="bg-green-500/10 text-green-400">Available</Badge>;
      case 'assigned': return <Badge className="bg-blue-500/10 text-blue-400">Assigned</Badge>;
      case 'reserved': return <Badge className="bg-amber-500/10 text-amber-400">Reserved</Badge>;
      case 'quarantined': return <Badge className="bg-red-500/10 text-red-400">Quarantined</Badge>;
      case 'porting_in': return <Badge className="bg-purple-500/10 text-purple-400">Porting In</Badge>;
      case 'porting_out': return <Badge className="bg-purple-500/10 text-purple-400">Porting Out</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getAssignmentStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500/10 text-green-400">Active</Badge>;
      case 'expired': return <Badge className="bg-amber-500/10 text-amber-400">Expired</Badge>;
      case 'revoked': return <Badge className="bg-red-500/10 text-red-400">Revoked</Badge>;
      case 'pending': return <Badge className="bg-blue-500/10 text-blue-400">Pending</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      mobile: 'bg-green-500/10 text-green-400',
      landline: 'bg-blue-500/10 text-blue-400',
      toll_free: 'bg-purple-500/10 text-purple-400',
      voip: 'bg-cyan-500/10 text-cyan-400',
      shortcode: 'bg-amber-500/10 text-amber-400',
    };
    return <Badge className={colors[type] || 'bg-slate-500/10 text-slate-400'}>{type.replace('_', ' ')}</Badge>;
  };

  if (loading && currentPage === 1) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Card className="glass">
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Number Management</h1>
          <p className="text-slate-400 mt-1">Manage phone number inventory and assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchNumbers} disabled={loading}>
            <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value="inventory" onValueChange={() => {}} className="space-y-4">
        <TabsList>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory">
          <Card className="glass">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle>Number Inventory</CardTitle>
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      placeholder="Search numbers..."
                      value={search}
                      onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                      className="pl-10"
                    />
                  </div>
                  <Select value={countryFilter} onValueChange={v => { setCountryFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Countries</SelectItem>
                      <SelectItem value="US">United States (+1)</SelectItem>
                      <SelectItem value="CA">Canada (+1)</SelectItem>
                      <SelectItem value="GB">United Kingdom (+44)</SelectItem>
                      <SelectItem value="AU">Australia (+61)</SelectItem>
                      <SelectItem value="DE">Germany (+49)</SelectItem>
                      <SelectItem value="FR">France (+33)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Status</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="quarantined">Quarantined</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Types</SelectItem>
                      <SelectItem value="mobile">Mobile</SelectItem>
                      <SelectItem value="landline">Landline</SelectItem>
                      <SelectItem value="toll_free">Toll Free</SelectItem>
                      <SelectItem value="voip">VoIP</SelectItem>
                      <SelectItem value="shortcode">Shortcode</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Capabilities</TableHead>
                    <TableHead>Monthly Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {numbers.length > 0 ? (
                    numbers.map(number => (
                      <TableRow key={number.id}>
                        <TableCell className="font-mono text-lg">{formatNumber(Number(number.phoneNumber))}</TableCell>
                        <TableCell>{number.providerName}</TableCell>
                        <TableCell>{number.countryCode}</TableCell>
                        <TableCell>{getTypeBadge(number.numberType)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {number.capabilities.includes('sms') && <Badge variant="outline" className="text-xs">SMS</Badge>}
                            {number.capabilities.includes('voice') && <Badge variant="outline" className="text-xs">Voice</Badge>}
                            {number.capabilities.includes('mms') && <Badge variant="outline" className="text-xs">MMS</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>{formatCurrency(number.monthlyCost)}/mo</TableCell>
                        <TableCell>{getStatusBadge(number.status)}</TableCell>
                        <TableCell>{number.assignedUser || '-'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {number.status === 'available' && providers.length > 0 && (
                              <Button variant="ghost" size="icon" className="text-green-400 hover:text-green-300" onClick={() => { setAssignNumberId(number.id); setAssignDialogOpen(true); }}>
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                            {number.status === 'assigned' && (
                              <Button variant="ghost" size="icon" className="text-amber-400 hover:text-amber-300" onClick={() => { setReleaseAssignmentId(assignments.find(a => a.numberPoolId === number.id)?.id || ''); setReleaseDialogOpen(true); }}>
                                <PhoneOff className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-slate-400">No numbers found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-400">Page {currentPage} of {totalPages}</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Number Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Phone Number</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Monthly Reward</TableHead>
                    <TableHead>Auto Renew</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assignments.length > 0 ? (
                    assignments.map(assignment => (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-white">{assignment.userEmail}</p>
                            <p className="text-xs text-slate-400">{assignment.userId}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-lg">{formatNumber(Number(assignment.phoneNumber))}</TableCell>
                        <TableCell>{getAssignmentStatusBadge(assignment.status)}</TableCell>
                        <TableCell className="font-mono text-green-400">{formatCurrency(assignment.monthlyReward)}/mo</TableCell>
                        <TableCell>{assignment.autoRenew ? <CheckCircle className="h-4 w-4 text-green-400" /> : <XCircle className="h-4 w-4 text-red-400" />}</TableCell>
                        <TableCell className="text-slate-400">{formatDateTime(assignment.assignedAt)}</TableCell>
                        <TableCell className="text-slate-400">{assignment.expiresAt ? formatDateTime(assignment.expiresAt) : 'Auto-renew'}</TableCell>
                        <TableCell className="text-right">
                          {assignment.status === 'active' && (
                            <Button variant="ghost" size="icon" className="text-amber-400 hover:text-amber-300" onClick={() => { setReleaseAssignmentId(assignment.id); setReleaseDialogOpen(true); }}>
                              <PhoneOff className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-400">No assignments found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">User ID</label>
              <Input placeholder="Enter user ID" value={assignUserId} onChange={e => setAssignUserId(e.target.value)} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Provider</label>
              <Select value={assignProviderId} onValueChange={(v) => setAssignProviderId(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="autoRenew" checked={assignAutoRenew} onChange={e => setAssignAutoRenew(e.target.checked)} className="rounded border-slate-600 text-cyan-500 focus:ring-cyan-500" />
              <label htmlFor="autoRenew" className="text-sm text-slate-300">Auto renew</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning}>
              {assigning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={releaseDialogOpen} onOpenChange={setReleaseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Release Number</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-300">Are you sure you want to release this number assignment? The number will become available for reassignment.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRelease} disabled={releasing}>
              {releasing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Release'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
